import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import axios from 'axios';
import { Spinner, Container, Card } from 'react-bootstrap';
import { FiCheckCircle, FiXCircle, FiTrendingUp } from 'react-icons/fi';

export default function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing' | 'success' | 'error'
  const [errorDetails, setErrorDetails] = useState('');

  useEffect(() => {
    const exchangeCode = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setErrorDetails(error || 'Access denied by user.');
        return;
      }

      if (!code) {
        setStatus('error');
        setErrorDetails('No authorization code returned from Google.');
        return;
      }

      try {
        // Retrieve current active Supabase session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setStatus('error');
          setErrorDetails('Your SeenByAI session expired. Please sign in again.');
          return;
        }

        // Post authorization code and state to our backend
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/auth/google/callback`,
          { code, state },
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.status === 'success') {
          setStatus('success');
          // Automatically redirect to dashboard after 2.5 seconds
          setTimeout(() => {
            const website = response.data.website;
            if (website) {
              navigate(`/dashboard?google_connected=true&website=${encodeURIComponent(website)}`);
            } else {
              navigate('/dashboard?google_connected=true');
            }
          }, 2500);
        } else {
          setStatus('error');
          setErrorDetails(response.data.message || 'Failed to exchange token.');
        }
      } catch (err) {
        console.error('Google Callback Error:', err);
        const backendMessage = err.response?.data?.detail || err.message;
        setStatus('error');
        setErrorDetails(backendMessage);
      }
    };

    exchangeCode();
  }, [searchParams, navigate]);

  return (
    <div 
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'radial-gradient(circle at 50% 50%, #1e1b4b 0%, #09090b 100%)',
        color: '#f4f4f5',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      <Container className="d-flex justify-content-center">
        <Card 
          style={{
            maxWidth: '460px',
            width: '100%',
            background: 'rgba(24, 24, 27, 0.75)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '24px',
            padding: '40px 30px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            textAlign: 'center'
          }}
        >
          <Card.Body className="p-0">
            {/* Visual Icon Header */}
            <div className="mb-4 d-flex justify-content-center">
              <div 
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'inset 0 2px 4px rgba(255,255,255,0.05)'
                }}
              >
                <FiTrendingUp size={32} color="#6366f1" />
              </div>
            </div>

            {status === 'processing' && (
              <>
                <h3 className="h4 fw-bold text-white mb-2">Connecting Google Account</h3>
                <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                  Please wait while we secure your connection details with Google.
                </p>
                <div className="d-flex justify-content-center my-4 py-2">
                  <Spinner 
                    animation="border" 
                    role="status" 
                    style={{ color: '#6366f1', width: '3rem', height: '3rem', borderWidth: '0.25em' }}
                  />
                </div>
                <p className="text-secondary mb-0" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>
                  EXCHANGING AUTH CODE...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="d-flex justify-content-center my-3">
                  <div style={{ animation: 'pulse 2s infinite' }}>
                    <FiCheckCircle size={64} color="#10b981" />
                  </div>
                </div>
                <h3 className="h4 fw-bold text-white mb-2 mt-4">Account Connected!</h3>
                <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                  Google Analytics & Search Console integration has been linked to your profile successfully.
                </p>
                <div 
                  className="p-3 mb-2 rounded-3" 
                  style={{ 
                    background: 'rgba(16, 185, 129, 0.05)', 
                    border: '1px solid rgba(16, 185, 129, 0.15)',
                    color: '#a7f3d0',
                    fontSize: '13px'
                  }}
                >
                  Redirecting back to dashboard...
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="d-flex justify-content-center my-3">
                  <FiXCircle size={64} color="#ef4444" />
                </div>
                <h3 className="h4 fw-bold text-white mb-2 mt-4">Connection Failed</h3>
                <p className="text-muted mb-4" style={{ fontSize: '14px' }}>
                  An error occurred during Google authorization.
                </p>
                <div 
                  className="p-3 mb-4 rounded-3 text-start" 
                  style={{ 
                    background: 'rgba(239, 68, 68, 0.05)', 
                    border: '1px solid rgba(239, 68, 68, 0.15)',
                    color: '#fca5a5',
                    fontSize: '13px',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace'
                  }}
                >
                  {errorDetails}
                </div>
                <button 
                  className="btn btn-outline-light w-100 py-2.5 fw-bold"
                  style={{ borderRadius: '10px', fontSize: '14px' }}
                  onClick={() => navigate('/dashboard')}
                >
                  Return to Dashboard
                </button>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
      
      {/* Keyframe animation styling */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
